import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Loader2, User, EyeOff } from "lucide-react";
import "../styles/print.css";
import { supabase } from "@/integrations/supabase/client";
import ReactMarkdown from 'react-markdown';
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import { useCharacterGuideCopy } from "@/lib/characterGuideCopy";
import ScriptViewToggle, { initialScriptView, saveScriptView } from "@/components/ScriptViewToggle";
import { castBriefLine } from "@/utils/castBriefLine";

interface CharacterAssignment {
  id: string;
  guest_name: string;
  guest_email: string;
  mystery_characters: {
    character_name: string;
    description: string;
    background: string;
    secret: string;
    introduction: string;
    introduction_pointform?: string | null;
    rumors: string;
    rumors_pointform?: string | null;
    accusations?: string | null;
    accusations_pointform?: string | null;
    round2_questions: string;
    round3_questions: string;
    round4_questions: string;
    // Legacy per-role columns (character-based mysteries)
    round2_innocent: string;
    round2_guilty: string;
    round2_accomplice: string;
    round3_innocent: string;
    round3_guilty: string;
    round3_accomplice: string;
    round4_innocent: string;
    round4_guilty: string;
    round4_accomplice: string;
    final_innocent: string;
    final_guilty: string;
    final_accomplice: string;
    // Unified columns (detective-style mysteries)
    round2_script?: string | null;
    round2_script_pointform?: string | null;
    round3_script?: string | null;
    round3_script_pointform?: string | null;
    round4_script?: string | null;
    round4_script_pointform?: string | null;
    final_statement?: string | null;
    final_statement_pointform?: string | null;
    relationships: any;
    secrets: any;
  };
}

type ScriptType = 'full' | 'pointForm' | 'both';

const CharacterAccess: React.FC = () => {
  const { t } = useTranslation();
  const characterGuideCopy = useCharacterGuideCopy();
  const { token } = useParams<{ token: string }>();
  const [assignment, setAssignment] = useState<CharacterAssignment | null>(null);
  const [scriptType, setScriptType] = useState<ScriptType>('full');
  const [hostScriptType, setHostScriptType] = useState<ScriptType>('full');
  const [gameOverview, setGameOverview] = useState<string | null>(null);
  // Predetermined ('detective') vs random-slip ('character'). We only surface a
  // "you are the murderer" banner for predetermined mysteries — in random-slip
  // games the culprit is drawn secretly at the table, so revealing the role here
  // would spoil it. See HostGuideTemplate "Running the Game" step 4 (Slip Draw).
  const [mysteryStyle, setMysteryStyle] = useState<string | null>(null);
  // Cast roster (name + public description only) so players can keep track of
  // who's who — ADR-0037 R3, via the token-scoped get_cast_by_token RPC.
  const [cast, setCast] = useState<Array<{ character_name: string; description: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (token) {
      loadCharacterAssignment();
    } else {
      setError("No access token provided");
      setLoading(false);
    }
  }, [token]);

  const loadCharacterAssignment = async () => {
    if (!token) {
      setError("Access token is required");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // The URL param is either the raw access_token UUID (email / canonical links)
      // or an 8-char short_code (copied /c/<code> links, ADR-0031). Resolve short
      // codes to the underlying UUID before hitting the token-keyed RPCs.
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      let accessToken = token;
      if (!UUID_RE.test(token)) {
        const { data: resolved, error: resolveError } = await supabase
          .rpc('get_token_by_short_code', { code: token });
        if (resolveError || !resolved) {
          console.error('Short-code resolve error:', resolveError);
          setError(t('characterAccess.errors.assignmentNotFound'));
          return;
        }
        accessToken = resolved as string;
      }

      // First, get the character assignment via secure RPC
      const { data: assignmentData, error: assignmentError } = await supabase
        .rpc('get_character_by_token', { access_token_param: accessToken })
        .single();

      if (assignmentError) {
        console.error('Assignment error:', assignmentError);
        setError(t('characterAccess.errors.assignmentNotFound'));
        return;
      }

      // Then, get the character details via secure RPC (validates token ownership)
      const { data: characterData, error: characterError } = await supabase
        .rpc('get_character_details', { char_id: assignmentData.character_id, access_token_param: accessToken })
        .single();

      if (characterError) {
        console.error('Character error:', characterError);
        setError(t('characterAccess.errors.dataNotFound'));
        return;
      }

      // Combine the data
      setAssignment({
        ...assignmentData,
        mystery_characters: characterData
      });

      // Fire-and-forget: records this access for party-cluster detection
      // (ADR-0128), which accelerates the "how did it go" follow-up email
      // once several guests are on their character pages at once. Never
      // blocks or fails the page load.
      supabase.rpc('touch_character_access', { access_token_param: accessToken }).then(
        ({ error: touchError }) => {
          if (touchError) console.error('touch_character_access error:', touchError);
        }
      );

      // Fetch the host's display preference (full / pointForm / both) for this packet
      const { data: meta } = await supabase
        .rpc('get_packet_metadata_by_token', { access_token_param: accessToken })
        .single();
      // NB: do not name this `t` — it would shadow the useTranslation() `t`
      // across this whole try block and put the error-branch t(...) calls above
      // in the temporal dead zone (crash: "Cannot access 'P' before initialization").
      const scriptPref = (meta as any)?.script_type;
      if (scriptPref === 'full' || scriptPref === 'pointForm' || scriptPref === 'both') {
        setHostScriptType(scriptPref);
        setScriptType(initialScriptView(scriptPref));
      }
      setMysteryStyle((meta as any)?.mystery_style ?? null);
      // Game overview from the host guide — leads the packet so every player
      // starts with the same scene-setting before their personal details
      const overview = (meta as any)?.game_overview;
      if (typeof overview === 'string' && overview.trim()) {
        setGameOverview(overview.trim());
      }

      // Cast roster (who's who) — names + public descriptions only, token-scoped.
      const { data: castData } = await supabase
        .rpc('get_cast_by_token', { access_token_param: accessToken });
      if (Array.isArray(castData)) {
        setCast(castData as Array<{ character_name: string; description: string }>);
      }
    } catch (error: any) {
      console.error('Error loading character assignment:', error);
      setError(`${t('characterAccess.errors.loadFailed')}: ${error.message || t('auth.errors.unknownError')}`);
    } finally {
      setLoading(false);
    }
  };

  const cleanMarkdownHeaders = (text: string): string => {
    return text
      .replace(/^If You're (Innocent|Guilty)( \(Final\))?:/gm, '')
      .replace(/Point form script: (•[^•]+)/g, (match, content) => {
        const items = content.split('•').filter(i => i.trim());
        return '\n' + items.map(item => `- ${item.trim()}`).join('\n');
      })
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  // Wrap a detailed prose body in straight quotes so it reads obviously as a
  // line to deliver aloud. Strips any leading inline bold "labels" the model
  // baked in (`**IF YOU'RE INNOCENT**`, `**WHEN ASKED ABOUT YOUR MOTIVE:**`)
  // since we already show the role label above. Skips wrapping when content
  // already starts with a bullet — bullets are tactical reminders, not speech.
  const wrapProseInQuotes = (text: string): string => {
    if (!text) return text;
    let body = text.trim();
    // Don't wrap bullet lists in quotes
    if (body.startsWith('-') || body.startsWith('•') || body.startsWith('*')) return text;
    // Don't wrap markdown headings as the spoken text
    if (body.startsWith('#')) return text;
    // Strip any number of leading inline bold "stage direction" headers that
    // the model added (e.g. **IF YOU'RE INNOCENT**, **When asked about ...:**)
    while (true) {
      const stripped = body.replace(/^\*\*[^*\n]+\*\*\s*\n+/, '');
      if (stripped === body) break;
      body = stripped;
    }
    body = body.trim();
    if (!body) return text;
    // Already wrapped? Leave alone.
    if (body.startsWith('"') && body.endsWith('"')) return body;
    return `"${body}"`;
  };

  // Format a "spoken" field that has detailed prose + optional point-form sibling.
  // Output respects the host's script_type choice:
  //   full      -> detailed only (prose wrapped in quotes for clarity)
  //   pointForm -> bullets only (with synthesized header from fallbackHeader if needed)
  //   both      -> detailed prose (in quotes), then "**Point Form:**" + bullets stacked underneath
  const formatScriptField = (
    detailed: string | null | undefined,
    pointForm: string | null | undefined,
    fallbackHeader: string,
  ): string => {
    const d = (detailed || '').trim();
    const p = (pointForm || '').trim();
    if (!d && !p) return '';
    if (scriptType === 'full' || !p) return d;
    if (scriptType === 'pointForm') {
      // Bullets don't carry their own section header — synthesize one if needed
      return p.startsWith('#') || p.startsWith('**') ? p : `## ${fallbackHeader}\n\n${p}`;
    }
    // 'both' — stack detailed (with its baked-in header) + point-form section
    if (!d) return p;
    return `${d}\n\n**Point Form:**\n\n${p}`;
  };

  const buildCharacterGuideContent = (character: any): string => {
    let content = `# ${character.character_name} - Your Character\n\n`;

    // Shared game overview first — content usually carries its own
    // `## Game Overview` heading; synthesize one if the host edited it away
    if (gameOverview) {
      content += gameOverview.startsWith('#')
        ? `${gameOverview}\n\n`
        : `## Game Overview\n\n${gameOverview}\n\n`;
    }

    // Cast roster — a quick "who's who" so players can keep everyone straight
    // (ADR-0037 R3). One brief line each; the player's own character is marked.
    if (cast.length > 1) {
      const rows = cast.map((c) => {
        const isSelf = c.character_name === character.character_name;
        return `- **${c.character_name}${isSelf ? ' (you)' : ''}** — ${castBriefLine(c.description)}`;
      }).join('\n');
      content += `## The Suspects — Who's Who\n\nA quick reference so you can keep everyone straight. Knowing who's who is not the same as knowing their secrets.\n\n${rows}\n\n`;
    }

    // Static character info — no pointform variants
    if (character.description) content += `${cleanMarkdownHeaders(character.description)}\n\n`;
    if (character.background) content += `${cleanMarkdownHeaders(character.background)}\n\n`;
    if (character.relationships && typeof character.relationships === 'string') {
      content += `${cleanMarkdownHeaders(character.relationships)}\n\n`;
    }
    if (character.secret) {
      content += `${cleanMarkdownHeaders(character.secret)}\n\n`;
      // Static stakes reminder (ADR-0037 G6): players who shrug off their secret
      // flatten the tension for everyone. Keep it generic — the generated secret
      // now carries its own specific consequences.
      content += `> ${characterGuideCopy.guardDirective}\n\n`;
    }

    // Round 1 setup — has pointform variants
    const introBlock = formatScriptField(
      character.introduction, character.introduction_pointform,
      'ROUND 1: YOUR INTRODUCTION',
    );
    if (introBlock) {
      // Static "what to do now" line for Round 1 (ADR-0037 R1)
      const introHeader = characterGuideCopy.roundHeaders.introduction;
      content += `> **${introHeader.title}.** ${introHeader.instruction}\n\n`;
      // Pull off the section heading, wrap the prose body in quotes, re-emit
      const m = introBlock.match(/^(##\s+[^\n]+\n+)([\s\S]*)$/);
      if (m && (scriptType === 'full' || (scriptType === 'both' && !introBlock.includes('**Point Form:**')))) {
        content += `${m[1]}${wrapProseInQuotes(m[2])}\n\n`;
      } else {
        content += `${cleanMarkdownHeaders(introBlock)}\n\n`;
      }
    }

    const rumorsBlock = formatScriptField(
      character.rumors, character.rumors_pointform,
      'RUMORS TO SPREAD',
    );
    if (rumorsBlock) {
      // Was previously missing here despite existing on the host's equivalent
      // view (ADR-0090 de-dup) — added for consistency, same pattern as Round 1.
      const rumorsHeader = characterGuideCopy.roundHeaders.rumors;
      content += `> **${rumorsHeader.title}.** ${rumorsHeader.instruction}\n\n`;
      content += `${cleanMarkdownHeaders(rumorsBlock)}\n\n`;
    }

    // For each role variant, strip its baked-in `## ROUND N:` heading so we don't
    // get duplicate round headers when innocent + guilty + accomplice are stacked.
    // We add a single round heading manually for the entire round block.
    const stripRoundHeader = (s: string | null | undefined): string => {
      if (!s) return '';
      return s.replace(/^##\s+ROUND\s+\d+[^\n]*\n+/i, '').trim();
    };

    // Rounds 2-4 — prefer unified `round_script` (detective-style); fall back to per-role
    // columns (character-based) when the unified column is absent. Both paths respect
    // the host's script_type choice (pointForm/full/both) via formatScriptField.
    // Static "what to do now" line per round, keyed to each round's fixed theme
    // (ADR-0037 R1/R2, shared copy via ADR-0090). Rendered only when the round
    // actually has content.
    const roundIntro: Record<2 | 3 | 4, { title: string; instruction: string }> = {
      2: characterGuideCopy.roundHeaders.round2,
      3: characterGuideCopy.roundHeaders.round3,
      4: characterGuideCopy.roundHeaders.round4,
    };

    const renderRound = (roundNum: 2 | 3 | 4) => {
      const questions = character[`round${roundNum}_questions`];
      const hasRoundContent = !!(
        questions ||
        character[`round${roundNum}_script`] ||
        character[`round${roundNum}_innocent`] ||
        character[`round${roundNum}_guilty`] ||
        character[`round${roundNum}_accomplice`]
      );
      if (hasRoundContent) content += `> **${roundIntro[roundNum].title}.** ${roundIntro[roundNum].instruction}\n\n`;
      if (questions) content += `${cleanMarkdownHeaders(questions)}\n\n`;

      const unified = character[`round${roundNum}_script`];
      const unifiedPF = character[`round${roundNum}_script_pointform`];
      if (unified) {
        const block = formatScriptField(unified, unifiedPF, `ROUND ${roundNum}`);
        if (block) {
          // For prose modes, wrap the body in quotes (after the section heading)
          const m = block.match(/^(##\s+[^\n]+\n+)([\s\S]*)$/);
          if (m && scriptType === 'full') {
            content += `${m[1]}${wrapProseInQuotes(m[2])}\n\n`;
          } else if (m && scriptType === 'both') {
            // Both mode: split prose from "**Point Form:**" section, wrap only the prose
            const split = m[2].split(/\n+\*\*Point Form:\*\*\n+/);
            if (split.length === 2) {
              content += `${m[1]}${wrapProseInQuotes(split[0])}\n\n**Point Form:**\n\n${split[1]}\n\n`;
            } else {
              content += `${cleanMarkdownHeaders(block)}\n\n`;
            }
          } else {
            content += `${cleanMarkdownHeaders(block)}\n\n`;
          }
        }
        return;
      }

      // Per-role variants (character-based mysteries). Each variant has its own
      // pointform sibling. We render a single round heading, then each role section
      // with the format the host picked.
      const variants: Array<{ label: string; key: string }> = [
        { label: "If You're Innocent:",      key: 'innocent' },
        { label: "If You're Guilty:",        key: 'guilty' },
        { label: "If You're an Accomplice:", key: 'accomplice' },
      ];
      const roleBlocks: string[] = [];
      for (const v of variants) {
        const detailed = character[`round${roundNum}_${v.key}`];
        const pointForm = character[`round${roundNum}_${v.key}_pointform`];
        if (!detailed && !pointForm) continue;
        const formatted = formatScriptField(detailed, pointForm, `ROUND ${roundNum}`);
        if (!formatted) continue;
        // Strip the variant's baked-in `## ROUND N` header — we'll add one round-level header above
        const cleaned = stripRoundHeader(formatted);
        // Wrap the prose body in quotes so it reads obviously as a script to deliver
        const quoted = wrapProseInQuotes(cleaned);
        roleBlocks.push(`**${v.label}**\n\n${quoted}`);
      }
      if (roleBlocks.length > 0) {
        content += `## ROUND ${roundNum}\n\n${roleBlocks.join('\n\n')}\n\n`;
      }
    };
    renderRound(2);
    renderRound(3);
    renderRound(4);

    // Final statement — prefer unified, fall back to per-role variants
    const hasFinal = !!(
      character.final_statement || character.final_innocent ||
      character.final_guilty || character.final_accomplice
    );
    if (hasFinal) {
      // final_statement (detective-style) permits confession-or-denial; final_innocent
      // (character-style, ADR-0065) is denial-only — genuinely different copy, not a
      // duplicate pair. Previously this page showed one generic note for both paths;
      // now picks the same copy the host's equivalent view uses (ADR-0090 de-dup).
      const finalHeader = character.final_statement
        ? characterGuideCopy.roundHeaders.finalStatement
        : characterGuideCopy.roundHeaders.finalInnocent;
      content += `> **${finalHeader.title}.** ${finalHeader.instruction}\n\n`;
    }
    if (character.final_statement) {
      const finalBlock = formatScriptField(
        character.final_statement, character.final_statement_pointform,
        'FINAL STATEMENT',
      );
      if (finalBlock) {
        const m = finalBlock.match(/^(##\s+[^\n]+\n+)([\s\S]*)$/);
        if (m && scriptType === 'full') {
          content += `${m[1]}${wrapProseInQuotes(m[2])}\n\n`;
        } else if (m && scriptType === 'both') {
          const split = m[2].split(/\n+\*\*Point Form:\*\*\n+/);
          if (split.length === 2) {
            content += `${m[1]}${wrapProseInQuotes(split[0])}\n\n**Point Form:**\n\n${split[1]}\n\n`;
          } else {
            content += `${cleanMarkdownHeaders(finalBlock)}\n\n`;
          }
        } else {
          content += `${cleanMarkdownHeaders(finalBlock)}\n\n`;
        }
      }
    } else {
      const finalVariants: Array<{ label: string; key: string }> = [
        { label: "If You're Innocent (Final):",      key: 'innocent' },
        { label: "If You're Guilty (Final):",        key: 'guilty' },
        { label: "If You're an Accomplice (Final):", key: 'accomplice' },
      ];
      const finalBlocks: string[] = [];
      for (const v of finalVariants) {
        const detailed = character[`final_${v.key}`];
        const pointForm = character[`final_${v.key}_pointform`];
        if (!detailed && !pointForm) continue;
        const formatted = formatScriptField(detailed, pointForm, 'FINAL STATEMENT');
        if (!formatted) continue;
        const cleaned = formatted.replace(/^##\s+FINAL[^\n]*\n+/i, '').trim();
        const quoted = wrapProseInQuotes(cleaned);
        finalBlocks.push(`**${v.label}**\n\n${quoted}`);
      }
      if (finalBlocks.length > 0) {
        content += `## FINAL STATEMENT\n\n${finalBlocks.join('\n\n')}\n\n`;
      }
    }

    // Accusations — has pointform variants (only populated for guilty/accomplice)
    if (character.accusations) {
      const accusationsHeader = characterGuideCopy.roundHeaders.accusations;
      content += `> **${accusationsHeader.title}.** ${accusationsHeader.instruction}\n\n`;
      const accBlock = formatScriptField(
        character.accusations, character.accusations_pointform,
        'ACCUSATIONS',
      );
      if (accBlock) content += `${cleanMarkdownHeaders(accBlock)}\n\n`;
    }

    return content;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p>{t('character.access.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !assignment) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader>
            <CardTitle className="text-center text-red-600">{t('character.access.accessDenied')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground">
              {error || t('character.access.characterNotFound')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const characterContent = buildCharacterGuideContent(assignment.mystery_characters);

  // Loud, unmissable role callout — only for predetermined ('detective') mysteries.
  // In random-slip ('character') games the culprit is drawn secretly at the table,
  // so we must NOT reveal it here. Only the guilty parties get a banner.
  const characterRole = (assignment.mystery_characters as any)?.character_role;
  const showRoleBanner =
    mysteryStyle === 'detective' &&
    (characterRole === 'murderer' || characterRole === 'accomplice');
  const roleBannerTitle =
    characterRole === 'murderer'
      ? t('characterAccess.roleBanner.murdererTitle')
      : t('characterAccess.roleBanner.accompliceTitle');
  const roleBannerBody =
    characterRole === 'murderer'
      ? t('characterAccess.roleBanner.murdererBody')
      : t('characterAccess.roleBanner.accompliceBody');

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <User className="h-6 w-6" />
            <h1 className="text-2xl font-bold">{t('character.access.welcome', { name: assignment.guest_name })}</h1>
          </div>
          <p className="text-muted-foreground">
            {t('characterAccess.subtitle')}
          </p>
          <Button
            onClick={() => window.print()}
            variant="outline"
            size="sm"
            className="mt-3 gap-2 print:hidden"
          >
            <Download className="h-4 w-4" />
            {t('mysteryPackage.export.saveAsPdf')}
          </Button>
        </div>

        {showRoleBanner && (
          <div
            role="alert"
            className="mb-6 rounded-lg border-2 border-red-600 bg-red-600/10 p-5 text-center"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <EyeOff className="h-6 w-6 text-red-600" />
              <h2 className="text-xl font-bold uppercase tracking-wide text-red-600">
                {roleBannerTitle}
              </h2>
            </div>
            <p className="leading-relaxed text-foreground">
              {roleBannerBody}
            </p>
          </div>
        )}

        {/* The tabs sit in a wrapper above the card, not inside it: Card clips its contents, which would stop them sticking. */}
        <div>
          <ScriptViewToggle
            sticky
            className="rounded-t-lg border border-b-0 border-border"
            value={scriptType}
            allowBoth={hostScriptType === 'both'}
            onChange={(v) => {
              setScriptType(v);
              saveScriptView(v);
            }}
          />
        <Card className="rounded-t-none">
          <CardContent className="p-6">
            <div className="mystery-content">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => (
                    <h1 className="text-2xl font-bold mb-4 text-primary">
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-xl font-semibold mb-3 text-secondary">
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-lg font-medium mb-2">
                      {children}
                    </h3>
                  ),
                  p: ({ children }) => (
                    <p className="mb-4 leading-relaxed">
                      {children}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-foreground">
                      {children}
                    </strong>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc list-inside mb-4 space-y-2">
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal list-inside mb-4 space-y-2">
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li className="ml-2">
                      {children}
                    </li>
                  ),
                }}
              >
                {buildCharacterGuideContent(assignment.mystery_characters)}
              </ReactMarkdown>
            </div>
          </CardContent>
        </Card>
        </div>

        <div className="character-packet-footer mt-8 text-center text-sm text-muted-foreground border-t border-border/40 pt-6">
          <p className="mb-1">
            <Trans
              i18nKey="characterAccess.footer.lovedPlaying"
              values={{ character: assignment.mystery_characters.character_name }}
              components={{ strong: <strong className="text-foreground" /> }}
            />
          </p>
          <p>
            {t('characterAccess.footer.hostCta')}{" "}
            <a
              href="https://www.mysterymaker.party/?utm_source=share&utm_medium=character_packet&utm_campaign=guest_footer"
              className="text-primary underline"
            >
              mysterymaker.party
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CharacterAccess;
